import { FC, useState } from 'react';
import { Avatar, FlexCell, FlexRow, Panel, Text } from '@epam/promo';
import { TextArea } from '@epam/uui-components';

import { TComment } from './types';
import { dateWithTimeToString } from 'hooks/helpers/date-time-helper';
import css from './Comments.module.scss';
import { CommentActions } from './CommentActions';

interface ICommentProps {
  comment?: TComment;
  isEditInProcess: boolean;
  currentUserId?: string;
  onConfirmEdit: (commentId: string, newValue: string) => void;
  onRejectEdit: (commentId: string) => void;
  onStartEdit: (commentId: string) => void;
  onRemove: (commentId: string) => void;
}

export const Comment: FC<ICommentProps> = ({
  comment,
  currentUserId,
  isEditInProcess,
  onConfirmEdit,
  onRejectEdit,
  onStartEdit,
  onRemove,
}) => {
  const [editedValue, setNewEditedValue] = useState(comment?.value || '');

  const authorName =
    [comment?.author.firstName, comment?.author.lastName]
      .filter(Boolean)
      .join(' ') || comment?.author.email;

  if (!comment) {
    return null;
  }

  const isEditable = !isEditInProcess && comment.authorId === currentUserId;

  const reset = (commentId: string) => {
    onRejectEdit(commentId);
    setNewEditedValue(comment.value);
  };

  return (
    <Panel shadow background="surface-main" cx={css.panel}>
      <FlexRow alignItems="center" columnGap="12" padding="12" size="48">
        <Avatar
          alt={authorName}
          img={comment.author.avatar ?? null}
          size="36"
        />
        <FlexCell cx={css.wide}>
          <FlexRow
            alignItems="center"
            columnGap="12"
            justifyContent="space-between">
            <FlexCell cx={css.wide}>
              <Text
                cx={css.noPadding}
                fontSize="14"
                fontWeight="600"
                lineHeight="18">
                {authorName}
              </Text>
              <Text
                color="gray60"
                cx={css.noPadding}
                fontSize="12"
                lineHeight="18">
                {dateWithTimeToString(comment.createdAt)}
              </Text>
            </FlexCell>

            <CommentActions
              comment={comment}
              editedValue={editedValue}
              isEditable={isEditable}
              onConfirmEdit={onConfirmEdit}
              onRejectEdit={reset}
              onRemove={onRemove}
              onStartEdit={onStartEdit}
            />
          </FlexRow>
        </FlexCell>
      </FlexRow>
      <FlexRow cx={css.wide} padding="12">
        {!comment.editMode && (
          <Text cx={css.noPadding} fontSize="14" lineHeight="24">
            {comment.value}
          </Text>
        )}
        {comment.editMode && (
          <TextArea
            autoFocus
            cx={css.editor}
            rows={3}
            value={editedValue ?? ''}
            onValueChange={setNewEditedValue}
          />
        )}
      </FlexRow>
    </Panel>
  );
};
